// src/pinball/types.ts - Core type definitions for the Pinball game

export interface Vector2D {
  x: number;
  y: number;
}

export interface PhysicsBody {
  id: string;
  position: Vector2D;
  velocity: Vector2D;
  radius: number;
  mass: number;
  isActive: boolean;
}

export interface FlipperBody {
  id: string;
  pivot: Vector2D;
  length: number;
  width: number;
  restAngle: number;
  activeAngle: number;
  currentAngle: number;
  angularVelocity: number;
  maxAngularVelocity: number;
  side: 'left' | 'right';
  isActive: boolean;
}

export interface StaticCollider {
  id: string;
  type: ColliderType;
  shape: CircleShape | SegmentShape | ArcShape | PolygonShape;
  restitution: number;
  elementRef: string;
}

export type ColliderType = 'circle' | 'segment' | 'arc' | 'polygon';

export interface CircleShape {
  center: Vector2D;
  radius: number;
}

export interface SegmentShape {
  start: Vector2D;
  end: Vector2D;
}

export interface ArcShape {
  center: Vector2D;
  radius: number;
  startAngle: number;
  endAngle: number;
}

export interface PolygonShape {
  vertices: Vector2D[];
}

// Game State
export interface PinballGameState {
  balls: BallState[];
  activeBallCount: number;
  score: number;
  scoreMultiplier: number;
  bonusMultiplier: number;
  currentBallNumber: number;
  totalBalls: number;
  ballsRemaining: number;
  ballSaveActive: boolean;
  ballSaveExpiry: number;
  multiballActive: boolean;
  tiltWarnings: number;
  tilted: boolean;
  dropTargetStates: Record<string, boolean>;
  laneCompletions: Record<string, boolean>;
  lastHitTimestamp: number;
  comboCount: number;
  leftFlipperActive: boolean;
  rightFlipperActive: boolean;
  plungerPower: number;
  plungerCharging: boolean;
  ballInLauncher: boolean;
  status: PinballGameStatus;
  isPaused: boolean;
  gameStartTime: number;
  elapsedTime: number;
}

export interface BallState {
  id: string;
  position: Vector2D;
  velocity: Vector2D;
  isActive: boolean;
}

export type PinballGameStatus = 'table_select' | 'ready' | 'playing' | 'paused' | 'game_over';

// Collision
export interface CollisionEvent {
  bodyId: string;
  elementId: string;
  elementType: TableElementType | 'wall' | 'flipper' | 'drain';
  contactPoint: Vector2D;
  contactNormal: Vector2D;
  relativeSpeed: number;
  timestamp: number;
}

export interface CollisionResult {
  normal: Vector2D;
  penetration: number;
  contactPoint: Vector2D;
}

export interface CollisionPair {
  bodyId: string;
  colliderId: string;
}

// Table Configuration
export interface TableThemeConfig {
  id: string;
  name: string;
  description: string;
  previewImage: string;
  width: number;
  height: number;
  elements: TableElementConfig[];
  flippers: FlipperConfig[];
  plunger: PlungerConfig;
  drain: DrainConfig;
  outlanes: OutlaneConfig[];
  inlanes: InlaneConfig[];
  walls: WallConfig[];
  scoring: ScoringConfig;
  jackpotCondition: JackpotCondition;
  multiballCondition: MultiballCondition;
  theme: VisualTheme;
}

export interface TableElementConfig {
  id: string;
  type: TableElementType;
  position: Vector2D;
  properties: ElementProperties;
}

export type TableElementType =
  | 'bumper' | 'pop_bumper'
  | 'slingshot'
  | 'target' | 'drop_target'
  | 'spinner'
  | 'ramp'
  | 'lane' | 'orbit'
  | 'gate';

export interface ElementProperties {
  radius?: number;
  width?: number;
  height?: number;
  angle?: number;
  path?: Vector2D[];
  restitution?: number;
  activeImpulse?: number;
  pointValue: number;
  group?: string;
  groupIndex?: number;
  color?: string;
  sprite?: string;
}

export interface FlipperConfig {
  id: string;
  side: 'left' | 'right';
  pivot: Vector2D;
  length: number;
  restAngle: number;
  activeAngle: number;
  activationTime: number;
  returnTime: number;
}

export interface PlungerConfig {
  position: Vector2D;
  laneWidth: number;
  laneHeight: number;
  maxLaunchSpeed: number;
  chargeTime: number;
}

export interface DrainConfig {
  position: Vector2D;
  width: number;
}

export interface OutlaneConfig {
  id: string;
  side: 'left' | 'right';
  path: Vector2D[];
}

export interface InlaneConfig {
  id: string;
  side: 'left' | 'right';
  path: Vector2D[];
}

export interface WallConfig {
  id: string;
  start: Vector2D;
  end: Vector2D;
  restitution: number;
}

export interface ScoringConfig {
  comboWindow: number;
  comboMultipliers: number[];
  endOfBallBonusBase: number;
  jackpotValue: number;
}

export interface JackpotCondition {
  type: 'all_ramps' | 'all_targets' | 'all_drop_banks' | 'custom';
  requiredElements: string[];
  description: string;
}

export interface MultiballCondition {
  type: 'target_bank' | 'ramp_combo' | 'lane_completion' | 'custom';
  requiredElements: string[];
  ballCount: number;
  description: string;
}

export interface VisualTheme {
  backgroundColor: string;
  playfieldColor: string;
  wallColor: string;
  ballColor: string;
  flipperColor: string;
  fontFamily: string;
  darkModeOverrides?: Partial<VisualTheme>;
}

// Physics Configuration
export interface PinballPhysicsConfig {
  gravity: number;
  maxBallSpeed: number;
  ballRadius: number;
  ballMass: number;
  defaultRestitution: number;
  rollingFriction: number;
  physicsSubsteps: number;
  spatialGridCellSize: number;
}

export const DEFAULT_PINBALL_PHYSICS: PinballPhysicsConfig = {
  gravity: 980,
  maxBallSpeed: 2000,
  ballRadius: 10,
  ballMass: 1,
  defaultRestitution: 0.6,
  rollingFriction: 0.992,
  physicsSubsteps: 2,
  spatialGridCellSize: 50,
};

// High Score and Statistics
export interface HighScoreEntry {
  initials: string;
  score: number;
  tableTheme: string;
  date: number;
}

export interface PinballStatistics {
  perTheme: Record<string, ThemeStatistics>;
  totalGamesPlayed: number;
  totalPlayTime: number;
}

export interface ThemeStatistics {
  highScore: number;
  averageScore: number;
  gamesPlayed: number;
  totalBallsPlayed: number;
  longestCombo: number;
  jackpotsHit: number;
}

export interface PinballGameResult {
  tableTheme: string;
  finalScore: number;
  ballsUsed: number;
  duration: number;
  jackpotsHit: number;
  maxCombo: number;
  maxMultiplier: number;
  completedAt: number;
}

// Sound Events
export type PinballSoundEvent =
  | 'flipper_activate' | 'flipper_release'
  | 'bumper_hit' | 'pop_bumper_hit'
  | 'target_hit' | 'drop_target_hit' | 'drop_target_bank_complete'
  | 'ramp_complete' | 'spinner_spin'
  | 'slingshot_hit'
  | 'drain' | 'plunger_release'
  | 'tilt_warning' | 'tilt'
  | 'multiball_start' | 'jackpot'
  | 'high_score' | 'ball_save_active'
  | 'combo';

// Visual Effects
export interface VisualEffect {
  type: 'flash' | 'trail' | 'celebration' | 'glow' | 'score_popup';
  position: Vector2D;
  startTime: number;
  duration: number;
  data?: Record<string, unknown>;
}
